import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useLinkCall } from '@/hooks/useCalling'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useRecordGroups } from '@/hooks/useRecordGroups'
import { searchRecords } from '@/services/api/record.service'
import type { Call } from '@/types/api/calling'
import { cn } from '@/utils/cn'
import { getErrorMessage } from '@/utils/error-parser'

/** Links a call from an unknown number to a record the user picks. */
export function LinkCallDialog({ call, onClose }: { call: Call; onClose: () => void }) {
  const [groupId, setGroupId] = useState<string>('')
  const [query, setQuery] = useState(call.leadNumber ?? '')
  const [recordId, setRecordId] = useState<string | null>(null)
  const debounced = useDebouncedValue(query, 300)
  const link = useLinkCall()

  const { recordGroups: groups } = useRecordGroups()
  const { data: results, isFetching } = useQuery({
    queryKey: ['call-link-search', groupId, debounced],
    queryFn: () => searchRecords(groupId, { q: debounced || undefined, includeSubgroups: true, page: 0, size: 10 }),
    enabled: !!groupId,
  })

  const submit = () => {
    if (!recordId) return
    link.mutate(
      { callId: call.id, recordId },
      {
        onSuccess: () => {
          toast.success('Call linked to the record')
          onClose()
        },
        onError: (error) => toast.error(getErrorMessage(error, 'Could not link the call')),
      }
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link call to a record</DialogTitle>
          <DialogDescription>Call from {call.leadNumber ?? 'an unknown number'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={groupId} onValueChange={(value) => { setGroupId(value); setRecordId(null) }}>
            <SelectTrigger aria-label="Record group">
              <SelectValue placeholder="Choose a record group" />
            </SelectTrigger>
            <SelectContent>
              {groups.filter((g) => !g.traceOnly).map((group) => (
                <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search records"
            aria-label="Search records"
            className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {groupId && !isFetching && results?.content.length === 0 && (
              <p className="py-4 text-center text-sm text-neutral-500">No matching records</p>
            )}
            {results?.content.map((record) => (
              <button
                key={record.recordId}
                type="button"
                onClick={() => setRecordId(record.recordId)}
                className={cn(
                  'w-full rounded-lg border px-3 py-2 text-left text-sm',
                  recordId === record.recordId ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:bg-neutral-50'
                )}
              >
                <span className="font-medium">{record.title ?? 'Untitled record'}</span>
                <span className="ml-2 text-xs text-neutral-500">{record.recordGroupName}</span>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!recordId || link.isPending}>Link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
